import { ReadDIQuery, ReadStudentContext, Table, getRuralDescription } from '@landscape/ui-lib'
import React from 'react'

import {
  challengeIndicatorsNationalColumns,
  challengeIndicatorsStateColumns,
  contextOverviewColumns,
  decileContextOverviewColumns,
  hsInfoColumns,
  satScoreInHighSchoolContextColumns,
} from './accessibilityPanelCols'

import get from 'lodash/get'
import isBoolean from 'lodash/isBoolean'

interface AccessibilityTableProps {
  props: {
    readStudentContext: ReadStudentContext
    readDIQuery: ReadDIQuery
  }
}

const decilePostscript: { [key: number]: string } = {
  1: 'st',
  2: 'nd',
  3: 'rd',
  4: 'th',
  5: 'th',
  6: 'th',
  7: 'th',
  8: 'th',
  9: 'th',
  10: 'th',
}

const getDecileValue = (value: number) => {
  if (!value) {
    return ''
  }

  let decileVal: number | undefined
  if (value % 10 === 0) {
    decileVal = value / 10
  } else {
    decileVal = Math.floor(value / 10) + 1
  }
  return `${decileVal}${decilePostscript[decileVal]}`
}

export const AccessibilityPanel: React.FC<AccessibilityTableProps> = (
  props: AccessibilityTableProps
) => {
  const readStudentContext = props.props.readStudentContext
  const getDIData = props.props.readDIQuery

  const { score_source, display_score, a_hs_satc_75, a_pct_taking_sat } = readStudentContext

  let isPOBox = get(readStudentContext, 'po_box') || false
  if (!isBoolean(isPOBox)) {
    // eslint-disable-next-line
    isPOBox = isPOBox == '1'
  }

  const contextOverview = [
    {
      a_hs_overrall_ptile: +readStudentContext.a_hs_overrall_ptile || 0,
      a_nh_overrall_ptile: +readStudentContext.a_nh_overrall_ptile || 0,
      di_hs_avg: getDIData ? +getDIData.di_hs_avg : 0,
      di_nh_avg: getDIData ? +getDIData.di_nh_avg : 0,
      po_box: isPOBox ? 'Yes' : 'No',
    },
  ]

  const decileContextOverview = [
    {
      a_hs_overall_decile: readStudentContext.a_hs_overall_decile || 0,
      a_nh_overall_decile: readStudentContext.a_nh_overall_decile || 0,
      di_hs_avg: getDIData ? getDecileValue(+getDIData.di_hs_avg) : 0,
      di_nh_avg: getDIData ? getDecileValue(+getDIData.di_nh_avg) : 0,
      po_box: isPOBox ? 'Yes' : 'No',
    },
  ]

  const satScoreInHighSchoolContext = [
    {
      a_hs_satc_25: +readStudentContext.a_hs_satc_25 || 0,
      a_hs_satc_50: +readStudentContext.a_hs_satc_50 || 0,
      a_hs_satc_75: +readStudentContext.a_hs_satc_75 || 0,
      display_score: +readStudentContext.display_score || 0,
      score_source: score_source === 'ACT' ? 'Yes' : 'No',
      above_75th: +display_score > parseInt(a_hs_satc_75, 10) ? 'Yes' : 'No',
      a_pct_taking_sat: +a_pct_taking_sat,
      less_than_25: +a_pct_taking_sat < 25 ? 'Yes' : 'No',
    },
  ]

  const hsInfo = [
    {
      rural: getRuralDescription(readStudentContext.rural),
      a_sr_class_size: isNaN(parseInt(readStudentContext.a_sr_class_size, 10))
        ? 'NA'
        : readStudentContext.a_sr_class_size,
      a_avg_frl_pct: isNaN(parseFloat(readStudentContext.a_avg_frl_pct))
        ? 'NA'
        : parseFloat(readStudentContext.a_avg_frl_pct).toFixed(0) + '%',
      a_avg_coll_sat_hs: isNaN(parseFloat(readStudentContext.a_avg_coll_sat_hs))
        ? 'NA'
        : parseFloat(readStudentContext.a_avg_coll_sat_hs).toFixed(0),
      a_pct_taking_sat: isNaN(parseInt(readStudentContext.a_pct_taking_ap, 10))
        ? 'NA'
        : readStudentContext.a_pct_taking_ap + '%',
      adv_aptaken: isNaN(parseFloat(readStudentContext.adv_aptaken))
        ? 'NA'
        : parseFloat(readStudentContext.adv_aptaken).toFixed(2),
      adv_avgapscore: isNaN(parseFloat(readStudentContext.adv_avgapscore))
        ? 'NA'
        : parseFloat(readStudentContext.adv_avgapscore).toFixed(2),
      adv_aptestavail: isNaN(parseFloat(readStudentContext.adv_aptestavail))
        ? 'NA'
        : parseFloat(readStudentContext.adv_aptestavail).toFixed(0),
      ddv_apcapstone: readStudentContext.ddv_apcapstone ? 'Yes' : 'NA',
    },
  ]

  const challengeIndicatorsNational = [
    {
      a_nh_college_access: readStudentContext.a_nh_college_access || 0,
      a_hs_college_access: readStudentContext.a_hs_college_access || 0,
      a_nh_family_ptile: readStudentContext.a_nh_family_ptile || 0,
      a_hs_family_ptile: readStudentContext.a_hs_family_ptile || 0,
      a_nh_medfaminc_ptile: readStudentContext.a_nh_medfaminc_ptile || 0,
      a_hs_medfaminc_ptile: readStudentContext.a_hs_medfaminc_ptile || 0,
      a_nh_housing_ptile: readStudentContext.a_nh_housing_ptile || 0,
      a_hs_housing_ptile: readStudentContext.a_hs_housing_ptile || 0,
      a_nh_educ_ptile: readStudentContext.a_nh_educ_ptile || 0,
      a_hs_educ_ptile: readStudentContext.a_hs_educ_ptile || 0,
      a_nh_crime_ptile: readStudentContext.a_nh_crime_ptile || 0,
      a_hs_crime_ptile: readStudentContext.a_hs_crime_ptile || 0,
      a_nh_overrall_ptile: readStudentContext.a_nh_overrall_ptile || 0,
      a_hs_overrall_ptile: readStudentContext.a_hs_overrall_ptile || 0,
    },
  ]

  const challengeIndicatorsState = [
    {
      st_a_nh_college_access: readStudentContext.st_a_nh_college_access || 0,
      st_a_hs_college_access: readStudentContext.st_a_hs_college_access_ptile || 0,
      st_a_nh_family_ptile: readStudentContext.st_a_nh_family_ptile || 0,
      st_a_hs_family_ptile: readStudentContext.st_a_hs_family_ptile || 0,
      st_a_nh_medfaminc_ptile: readStudentContext.st_a_nh_medfaminc_ptile || 0,
      st_a_hs_medfaminc_ptile: readStudentContext.st_a_hs_medfaminc_ptile || 0,
      st_a_nh_housing_ptile: readStudentContext.st_a_nh_housing_ptile || 0,
      st_a_hs_housing_ptile: readStudentContext.st_a_hs_housing_ptile || 0,
      st_a_nh_educ_ptile: readStudentContext.st_a_nh_educ_ptile || 0,
      st_a_hs_educ_ptile: readStudentContext.st_a_hs_educ_ptile || 0,
      st_a_nh_crime_ptile: readStudentContext.st_a_nh_crime_ptile || 0,
      st_a_hs_crime_ptile: readStudentContext.st_a_hs_crime_ptile || 0,
      st_a_nh_overrall_ptile: readStudentContext.st_a_nh_overrall_ptile || 0,
      st_a_hs_overrall_ptile: readStudentContext.st_a_hs_overrall_ptile || 0,
    },
  ]

  return (
    <>
      <Table
        id="decileContextOverview-table"
        title="Decile Context Overview"
        titlePosition="start"
        data={decileContextOverview}
        columns={decileContextOverviewColumns}
        defaultSortAsc={false}
        condensed
        accessibilityMode
        uniqueKeyColumn="a_hs_overall_decile"
      />
      <Table
        id="contextOverview-table"
        title="1-100 Context Overview"
        titlePosition="start"
        data={contextOverview}
        columns={contextOverviewColumns}
        defaultSortAsc={false}
        condensed
        accessibilityMode
        uniqueKeyColumn="a_hs_overrall_ptile"
      />
      <Table
        id="satScoreInHighSchoolContext-table"
        title="SAT Score in High School Context"
        titlePosition="start"
        data={satScoreInHighSchoolContext}
        columns={satScoreInHighSchoolContextColumns}
        defaultSortAsc={false}
        condensed
        accessibilityMode
        uniqueKeyColumn="a_hs_satc_25"
      />
      <Table
        id="hsInfo-table"
        title="High School Information"
        titlePosition="start"
        data={hsInfo}
        columns={hsInfoColumns}
        defaultSortAsc={false}
        condensed
        accessibilityMode
        uniqueKeyColumn=""
      />
      <Table
        id="challengeIndicatorsNational-table"
        title="Challenge Indicators for National"
        titlePosition="start"
        data={challengeIndicatorsNational}
        columns={challengeIndicatorsNationalColumns}
        defaultSortAsc={false}
        condensed
        accessibilityMode
        uniqueKeyColumn="a_nh_college_access"
      />
      <Table
        id="challengeIndicatorsState-table"
        title="Challenge Indicators for State"
        titlePosition="start"
        data={challengeIndicatorsState}
        columns={challengeIndicatorsStateColumns}
        defaultSortAsc={false}
        condensed
        accessibilityMode
        uniqueKeyColumn="st_a_nh_college_access"
      />
    </>
  )
}
